import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { logOut } from "../features/auth/authSlice";
import secret from "../secret.json";
import { isUnlimitedCapacity } from "../utils/planUtils";
import { academyNameOf, useAdminView } from "./useAdminView";
import TelegramConnect from "./TelegramConnect";
import AppInstallMenuItem from "./AppInstallMenuItem";
import "./UserMenu.css";

// 상단 바 오른쪽의 로그인 계정 표시. 누구로 들어와 있는지 보이고 로그아웃할 수 있다.
const UserMenu = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const containerRef = useRef(null);
  // fuddl3297 이면 이 메뉴에서 학원을 골라 그 학원 화면을 본다(useAdminView.js)
  const { isViewing, canView, academyList, message, viewAs, goBack } = useAdminView({ loadList: isOpen });
  const normalizedKeyword = keyword.trim().toLowerCase();
  // 서버가 최근 3개월 안에 로그인한 계정만, 최근 로그인 순으로 준다. 순서를 그대로 쓴다.
  const shownAcademyList = academyList.filter((one) =>
    !normalizedKeyword ||
    `${academyNameOf(one)} ${one.name || ""} ${one.nickname || ""}`.toLowerCase().includes(normalizedKeyword)
  );

  // 메뉴 밖을 누르면 닫는다.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const closeOnOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isOpen]);

  // 학원 계정에 묶인 선생님은 학원 계정으로 들어오지만 이름은 본인 것(acting_name)을 보인다
  const displayName = isViewing
    ? `${academyNameOf(user)} 보는 중`
    : user.acting_name || user.name || user.nickname || "선생님";
  const organizationName = user.organization_name || "";

  // 서버가 주는 plan 은 basic/plus/premium 처럼 전부 소문자라 그대로 쓰면 이름처럼 안 읽힌다.
  const planLabel = user.plan ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1) : "";
  // 정원 소진율. 학생 수는 /api/v1/user/me 가 student_count 로 함께 준다.
  const capacityLabel = isUnlimitedCapacity(user.max_student_capacity)
    ? `(${user.student_count ?? 0}명 · 무제한)`
    : user.max_student_capacity
    ? `(${user.student_count ?? 0}/${user.max_student_capacity}명)`
    : `(${user.student_count ?? 0}명)`;

  const handleLogOut = () => {
    if (!window.confirm("로그아웃하시겠습니까?")) {
      return;
    }
    setIsOpen(false);
    dispatch(logOut());

    // 우리 토큰만 지우면 소용이 없다. 로그인이 지인에듀 OAuth 라서, 그쪽 세션이 살아
    // 있으면 다음 로그인 화면에서 곧바로 승인돼 되돌아온다. 모 서비스도 함께 끊는다.
    window.location.href = `${secret.GINEDU_BASE_URL}/accounts/logout/`;
  };

  return (
    <div ref={containerRef} className="user-menu">
      <button
        type="button"
        className={`user-menu__trigger ${isOpen ? "user-menu__trigger--open" : ""}`}
        onClick={() => setIsOpen((previous) => !previous)}
      >
        <span className="user-menu__name">{displayName}</span>
        <span className="user-menu__caret">▼</span>
      </button>

      {isOpen && (
        <div className="user-menu__dropdown">
          <div className="user-menu__summary">
            <div className="user-menu__display-name">{displayName}</div>
            {organizationName && (
              <div className="user-menu__organization">{organizationName}</div>
            )}
            {planLabel && (
              <div className="user-menu__plan">
                {planLabel} 플랜{" "}
                <span className="user-menu__capacity">{capacityLabel}</span>
              </div>
            )}
          </div>

          {!isViewing && <TelegramConnect />}

          {canView && (
            <div className="user-menu__academy">
              {isViewing && (
                <button type="button" className="user-menu__back" onClick={goBack}>
                  운영진 계정으로 돌아가기
                </button>
              )}
              <div className="user-menu__academy-title">학원 화면 보기</div>
              <input
                className="user-menu__academy-search"
                value={keyword}
                placeholder="학원 이름이나 아이디로 찾기"
                onChange={(event) => setKeyword(event.target.value)}
              />
              <div className="user-menu__academy-list">
                {shownAcademyList.map((one) => (
                  <button
                    key={one.id}
                    type="button"
                    className={`user-menu__academy-item${one.id === user.id ? " user-menu__academy-item--current" : ""}`}
                    onClick={() => viewAs(one.id)}
                  >
                    <b>{academyNameOf(one)}</b>
                    <span>
                      {[one.name, one.nickname, one.plan]
                        .filter((part, index, list) => part && part !== academyNameOf(one) && list.indexOf(part) === index)
                        .join(" · ")}
                    </span>
                  </button>
                ))}
                {!shownAcademyList.length && <div className="user-menu__academy-empty">{message || "찾는 학원이 없어요."}</div>}
              </div>
            </div>
          )}

          <AppInstallMenuItem />

          <button
            type="button"
            className="user-menu__item user-menu__item--danger"
            onClick={handleLogOut}
          >
            로그아웃
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
